import { inject, Injectable } from '@angular/core';

import { v4 as UUIDv4 } from 'uuid';

import { Constants } from 'app/constants/constants';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { WidgetPredictiveOptions } from '../components/widget-predictive/widget-predictive.component';

@EnableClassTelemetry()
@Injectable()
export class WidgetService {
  private readonly logger = inject(LoggerService);

  createWidgetStub(type: string, dashboard: Dashboard, dataSourceId?: string): Widget {
    return {
      id: UUIDv4(),
      component: '',
      title: '',
      dataSourceId,
      payload: {
        projectId: dashboard.project_id,
        excludedValues: [],
        selectedItems: [],
        sentimentScale: dashboard.payload.sentimentScale,
        type,
      },
      x: 0,
      y: 0,
      cols: 6,
      rows: 8,
      properties: {
        is_locked: false,
        as_data_table: false,
        as_document_list: false,
      },
    };
  }

  createWidget(
    type: string,
    dashboard: Dashboard,
    dataSourceId?: string,
    isOverTime?: boolean,
    widgetPayload?: any
  ): Widget {
    const widget = this.createWidgetStub(type, dashboard, dataSourceId);

    switch (type) {
      case 'documentCount':
        widget.component = 'DashboardWidgetChartComponent';
        if (isOverTime) {
          widget.payload.aggregator = 'time';
          widget.payload.dateInterval = 'day';
          widget.payload.graphType = 'spline';
        } else {
          widget.payload.aggregator = 'total';
          widget.payload.graphType = 'column';
        }
        break;
      case 'sentimentScore':
        widget.component = 'DashboardWidgetChartComponent';
        if (isOverTime) {
          widget.payload.aggregator = 'AVG';
          widget.payload.graphType = 'spline';
          widget.payload.dateInterval = 'day';
        } else {
          widget.payload.aggregator = 'total_AVG';
          widget.payload.graphType = 'column';
        }
        break;
      case 'sentimentVolume':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.graphType = 'stackedbar';
        widget.payload.aggregator = 'time';
        widget.payload.dateInterval = 'day';
        break;
      case 'numericMetadata':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.dateInterval = 'day';
        widget.payload.graphType = 'spline';
        widget.payload.newspaper = { show: false };
        break;
      case 'gauge':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.graphType = 'gauge';
        widget.payload.gaugeOptions = {
          metric: 'avg',
          minRating: 1,
          maxRating: 10,
          decimalPrecision: 2,
          increaseColor: 'gray',
          decreaseColor: 'gray',
          showAs: 'absolute',
          scoring: 'rating',
        };
        break;
      case 'scatterPlot':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.xAxis = 'sentiment';
        widget.payload.yAxis = 'volume';
        widget.payload.graphType = 'scatter';
        break;
      case 'sentimentPolarity':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.aggregator = 'total';
        widget.payload.graphType = 'column';
        break;
      case 'wordcloud':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.orderBySentiment = false;
        break;
      case 'topMovers':
        widget.component = 'DashboardWidgetTopMoversComponent';
        widget.payload.aggregator = 'time';
        widget.payload.dateInterval = 'day';
        widget.payload.graphType = 'spline';
        break;
      case 'taxonomyMap':
        widget.component = 'WidgetTaxonomyMapComponent';
        widget.payload.limit = 10;
        widget.cols = 9;
        break;
      case 'note':
        widget.component = 'DashboardWidgetNotesComponent';
        widget.payload.text = 'Add your note here';
        break;
      case 'noteMarkdown':
        widget.component = 'DashboardWidgetMarkdownComponent';
        widget.payload.text = 'Add your note here';
        break;
      case 'documents':
        widget.component = 'DashboardWidgetDocumentListComponent';
        widget.payload.isCardView = false;
        break;
      case 'crosstab':
        widget.component = 'DashboardWidgetCrosstabComponent';
        widget.payload.display = {
          heatmap: true,
        };
        widget.payload.columnSelectedItems = [];
        widget.payload.graphType = 'crosstab';
        widget.payload.display.decimalPrecision = '0';
        break;
      case 'featureScoring':
        widget.component = 'DashboardWidgetFeatureScoringComponent';
        widget.payload.analyticItem = 'topics';
        break;
      case 'sentimentAccuracy':
        widget.component = 'DashboardWidgetSentimentAccuracyComponent';
        break;
      case 'impact':
        widget.component = 'DashboardWidgetImpactComponent';
        widget.payload.limit = 5;
        widget.payload.analyticItem = 'topics';
        widget.payload.entityTypeFilter = widgetPayload.entityTypeFilter;
        widget.payload.isSpecial = false;
        widget.payload.sort = {
          field: 'right',
          order: 'desc',
        };
        break;
      case 'bigNumber':
        widget.component = 'DashboardWidgetBigNumberComponent';
        widget.payload.aggregator = 'avg';
        widget.payload.analyticItem = 'documents';
        break;
      case 'donut':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.limit = 20;
        widget.payload.graphType = 'pie';
        widget.payload.donutOptions = {
          metric: 'avg',
          minRating: 1,
          maxRating: 10,
          decimalPrecision: 2,
          increaseColor: 'gray',
          decreaseColor: 'gray',
          showAs: 'absolute',
          scoring: 'rating',
        };
        break;
      case 'grid':
        widget.component = 'DashboardWidgetGridComponent';
        widget.payload.sort = {
          field: 'breakdown',
          dir: 'asc',
        };
        break;
      case 'breakdown':
        widget.component = 'DashboardWidgetChartComponent';
        widget.payload.graphType = 'column';
        widget.payload.limit = 20;
        break;
      case 'predictor':
        widget.component = 'DashboardWidgetPredictiveComponent';

        const predictivePayload = widget.payload as WidgetPredictiveOptions;

        predictivePayload.simulations = [];
        predictivePayload.stepValue = Constants.TWTRA_DEFAULT_STEP_VALUE;
        predictivePayload.decimals = Constants.TWTRA_DEFAULT_DECIMALS;
        predictivePayload.view = Constants.TWTRA_DEFAULT_VIEW;
        predictivePayload.scatterValue = Constants.TWTRA_DEFAULT_SCATTER_VALUE;
        predictivePayload.displayLabels = Constants.TWTRA_DEFAULT_DISPLAY_LABELS;
        predictivePayload.mainSettings = {
          scoring: Constants.TWTRA_DEFAULT_SCORING_STRATEGY,
          min: Constants.TWTRA_DEFAULT_MIN_SCALE,
          max: Constants.TWTRA_DEFAULT_MAX_SCALE,
        };
        predictivePayload.driverSettings = {
          scoring: Constants.TWTRA_DEFAULT_SCORING_STRATEGY,
          min: Constants.TWTRA_DEFAULT_MIN_SCALE,
          max: Constants.TWTRA_DEFAULT_MAX_SCALE,
        };
        break;
      case 'powerScore':
        widget.payload.analyticItem = 'entities';
        widget.title = 'Power score';
        if (isOverTime) {
          widget.component = 'DashboardWidgetChartComponent';
          widget.payload.aggregator = 'AVG';
          widget.payload.graphType = 'spline';
          widget.payload.dateInterval = 'day';
        } else {
          widget.component = 'DashboardWidgetBigNumberComponent';
          widget.payload.aggregator = 'avg';
          widget.payload.analyticItem = 'documents';
          widget.payload.metadataDisplayNameOne = 'Power score';
        }
        break;
      case 'powerScoreOverTime':
        widget.payload.analyticItem = 'entities';
        widget.title = 'Power score over time';
        if (isOverTime) {
          widget.component = 'DashboardWidgetChartComponent';
          widget.payload.aggregator = 'AVG';
          widget.payload.graphType = 'spline';
          widget.payload.dateInterval = 'day';
        }
        break;
      case 'focusAreas':
        widget.component = 'DashboardWidgetImpactComponent';
        widget.title = 'Focus Areas';
        widget.rows = 4;
        widget.minItemRows = 4;
        widget.minItemCols = 3;
        widget.payload.limit = 3;
        widget.payload.entityTypeFilter = widgetPayload.entityTypeFilter;
        widget.payload.isSpecial = false;
        widget.payload.sort = {
          field: 'right',
          order: 'asc',
        };
        break;
      case 'insightFeed':
        widget.component = 'DashboardWidgetInsightFeedComponent';
        widget.payload.type = 'insightFeed';
        widget.title = 'Insight Feed';
        delete widget.dataSourceId;
        break;
      case 'journey':
        widget.component = 'DashboardWidgetJourneyComponent';
        widget.title = 'Journey';
        widget.payload.graphType = 'spline';
        widget.payload.selectedRelativeRange = 'all';
        widget.cols = 12;
        break;
      default:
        this.logger.error(`Unknown widget type '${type}'`);
        return;
    }

    return widget;
  }

  createNoteWidget(dashboard: Dashboard, text?: string): Widget {
    const widget = this.createWidget('note', dashboard);
    if (text) {
      widget.payload.text = text;
    }
    return widget;
  }
}
